/**
 * Centralized event name constants for Google Analytics.
 *
 * Usage:
 *   import { EVENTS } from "utils/analytics.events";
 *   import { trackEvent } from "utils/analytics";
 *   trackEvent(EVENTS.AUTH.LOGIN_EMAIL);
 *
 * Notes:
 * - Event name strings must NOT be changed without coordinating with GA4 dashboards/reports.
 * - Dynamic events (using template literals) are not included here — use trackEvent() directly.
 * - New events should be added here before use.
 */

export const EVENTS = {
  // Auth
  AUTH: {
    LOGIN_EMAIL: "login_email_login_btn",
    LOGIN_GOOGLE: "login_with_google",
    // Canonical GA4 signup event — fired on EVERY signup-completion path with a
    // `method` param (google | email | sso | invite). Funnels/reports should key
    // on this single event; it maps to GA4's recommended `sign_up` event. The
    // per-method events below are kept for existing dashboards. See trackSignUpCompleted.
    SIGNUP: "sign_up",
    SIGNUP_EMAIL: "signup_email_signup_btn",
    SIGNUP_GOOGLE: "google_sign_up",
    SIGNUP_SSO: "signup_sso",
    GOOGLE_LOGIN: "google_login",
    LOGOUT: "logout_click",
  },

  // Design
  DESIGN: {
    CREATE: "Created_Design",
    NEW: "New_Design",
    EDIT: "Design_Edit",
    ENHANCED: "Design_Enhanced",
    ANIMATE: "Animate_Design",
    LIKE: "design_like_click",
    DELETE: "design_delete_click",
    DOWNLOAD: "design_download_click",
    ERASE: "design_erase_click",
    AI_ASSISTANT_TOGGLE: "design_ai_assistant_toggle",
    ASPECT_RATIO_TOGGLE: "design_aspect_ratio_toggle",
    BACKGROUND_EDIT: "design_background_edit_click",
    COLOR_EDITING: "design_color_editing_click",
    FABRIC_CHANGE: "design_fabric_change_click",
    GLAM_ROOM: "design_glam_room_click",
    MATERIAL_CHANGE: "design_material_change_click",
    POSE_EDIT: "design_pose_edit_click",
    PRINT_CHANGE: "design_print_change_click",
    TAILORING: "design_tailoring_click",
    SKETCH_POSE: "design_sketch_pose_click",
    ADD_DETAIL_LOGO: "design_add_detail_logo_click",
    REFERENCE_FABRIC: "design_reference_fabric_click",
    REFERENCE_INSPIRATION: "design_reference_inspiration_click",
    ANIMATE_DESIGN: "design_animate_design_click",
    // Screen actions
    SCREEN_DELETE_IMAGE: "design_screen_delete_image",
    SCREEN_DOWNLOAD: "design_screen_design_download",
    SCREEN_SAVE_COLLECTION: "design_screen_design_save_collection",
    SCREEN_MP4_DOWNLOAD: "design_screen_mp4_download",
    SCREEN_PLM_SOLUTION: "design_screen_plm_solution_button",
    SCREEN_PROMPT_ASSISTANT: "design_screen_prompt_assistant_opened",
    SCREEN_REFERENCE_UPLOADED: "design_screen_referance_image_uploaded",
    SCREEN_SELECT_TRAINING: "design_screen_select_training",
    SCREEN_SKETCH_UPLOADED: "design_screen_sketch_uploaded",
    SCREEN_VIEW_EXAMPLES: "design_screen_view_examples_clicked",
    SCREEN_FINE_TUNE_TRY: "design_screen_fine_tune_try",
    SCREEN_HEADER_SAVE_COLLECTION: "design_screen_header_save_collection_button",
    // Edit menu
    EDIT_MENU_ADD_DETAIL: "design_screen_edit_menu_add_detail",
    EDIT_MENU_ANIMATE: "design_screen_edit_menu_animate_design",
    EDIT_MENU_BACKGROUND: "design_screen_edit_menu_background_edit",
    EDIT_MENU_CHANGE_ATTRIBUTE: "design_screen_edit_menu_change_attribute",
    EDIT_MENU_ERASE: "design_screen_edit_menu_erase",
    EDIT_MENU_GLAM_ROOM: "design_screen_edit_menu_glam_room",
    EDIT_MENU_MAKE_SEAMLESS: "design_screen_edit_menu_make_seamless",
    EDIT_MENU_POSE_EDIT: "design_screen_edit_menu_pose_edit",
    EDIT_MENU_TAILORING: "design_screen_edit_menu_tailoring",
    EDIT_MENU_VARIATE: "design_screen_edit_menu_variate_design",
    EDIT_MENU_PLUS_AI: "design_edit_menu_attribute_plusAI_activated",
    // Edit screen
    EDIT_CHANGE_COLOR: "design_screen_edit_change_color",
    EDIT_CHANGE_FABRIC: "design_screen_edit_change_fabric",
    EDIT_CHANGE_PATTERN: "design_screen_edit_change_pattern",
    // History
    HISTORY_SEARCH: "design_history_search",
    HISTORY_IMAGE_CLICK: "design_history_image_click",
    HISTORY_IMAGE_HOVER_EDIT: "design_history_image_hover_edit",
    HISTORY_IMAGE_HOVER_SAVE: "design_history_image_hover_save_collection",
    HISTORY_IMAGE_HOVER_EXPORT_BROWZWEAR: "design_history_image_hover_export_browzwear",
    HISTORY_FILTER_FAVORITES: "design_history_filter_favorites",
    HISTORY_FILTER_POPULAR: "design_history_filter_most_popular",
    HISTORY_FILTER_RECENT: "design_history_filter_most_recent",
    HISTORY_CAROUSEL_DOWNLOAD: "design_history_carousel_image_download",
    HISTORY_CAROUSEL_EDIT: "design_history_carousel_image_edit",
    HISTORY_CAROUSEL_EDITOR: "design_history_carousel_image_open_in_editor_button",
    HISTORY_CAROUSEL_SAVE: "design_history_carousel_image_save_collection",
  },

  // Photoshoot
  PHOTOSHOOT: {
    CREATE: "Photoshoot_Create",
    CREATE_CLICK: "photoshoot_create_click",
    NEW: "New_Photoshoot",
    PAGE_VIEW: "photoshoot_page_view",
    SELECT_MODEL: "photoshoot_select_model_click",
    SELECT_POSE: "photoshoot_select_pose_click",
    ANGLE_SELECT: "photoshoot_angle_select",
    PROMPT_EDIT: "photoshoot_prompt_edit_click",
    PROMPT_TAB: "photoshoot_prompt_tab_click",
    TEMPLATE_TAB: "photoshoot_template_tab_click",
    ASSET_TAB: "photoshoot_asset_tab_click",
    ASSETS_ADD: "photoshoot_assets_add_btn",
    SAVE: "photoshoot_save_click",
    USE_IMAGE: "photoshoot_use_image_click",
    USER_DRAW_MASK: "photoshoot_user_draw_mask",
    ZOOM_OUT: "photoshoot_zoom_out_button",
    TWO_PIECE_TOGGLE: "photoshoot_two_piece_toggle",
    TYPE_2_PIECES: "photoshoot_type_2_pieces_selected",
    TYPE_DRESS: "photoshoot_type_dress_selected",
    UPLOAD_SINGLE: "photoshoot_upload_single",
    ANIMATE_DESIGN: "photoshoot_animate_design_button",
    MANNEQUIN_SELECT: "photoshoot_mannequin_select",
    MANNEQUIN_DOWNLOAD_ZIP: "photoshoot_mannequin_download_zip",
    HISTORY_DOWNLOAD_ZIP: "photoshoot_history_page_download_zip",
    AUTO_FILL: "photoshoot_auto_fill_btn",
    // List items
    ITEM_DOWNLOAD: "photoshoots_item_download",
    ITEM_EDIT: "photoshoots_item_edit",
    ITEM_SAVE_COLLECTION: "photoshoots_item_save_collection",
    FILTER_FAVORITES: "photoshoots_filter_favorites",
    FILTER_POPULAR: "photoshoots_filter_most_popular",
    FILTER_RECENT: "photoshoots_filter_most_recent",
    SELECTED_MODEL_DOWNLOAD: "photoshoot_selected_model_download",
    // Mannequin
    MANNEQUIN_AUTO_FILL: "mannequin_photoshoot_auto_fill_btn",
    MANNEQUIN_ASSET_SELECT: "mannequin_asset_select",
    MANNEQUIN_BACKGROUND_SELECT: "mannequin_background_select",
    MANNEQUIN_UPLOAD_IMAGE2PROMPT: "mannequin_photoshoot_promptarea_upload_image2prompt",
    MANNEQUIN_PAGE_VIEW: "mannequin_page_view",
    MANNEQUIN_SUBMIT_ACCEPTED: "mannequin_submit_accepted",
    MANNEQUIN_SUBMIT_ERROR: "mannequin_submit_error",
    MANNEQUIN_HAND_ENHANCE_START: "mannequin_hand_enhance_start",
    // Generation lifecycle — fired by the legacy PhotoshootStatusListener, deleted in REF-6599; no current emitter
    GENERATION_COMPLETE: "ps_generation_complete",
    GENERATION_ERROR: "ps_generation_error",
  },

  // Training
  TRAINING: {
    NEW: "New_Training",
    NEW_CLICK: "new_training_click",
    STYLE: "Style_Training",
    FABRIC: "Fabric_Training",
    ARTISTIC_SKETCH: "ArtisticSketch_Create",
    TECHPACK_SKETCH: "TechpackArtisticSketch_Create",
    BRAND_DNA_CLICK: "new_training_brand_dna_click",
    BRAND_DNAS_SEE_ALL: "new_training_brand_dnas_see_all",
    TRENDS_SEE_ALL: "new_training_trends_see_all",
    SEARCH: "trainings_search",
    FAVOURITE_TOGGLE: "train_favourite_toggle",
    FILTER_CLICK: "train_filter_click",
    SEARCH_SUBMIT: "train_search_submit",
    SORT_CLICK: "train_sort_click",
    // My trainings
    MY_CREATE_DESIGN_WITH: "mytrainings_create_design_with",
    MY_DELETE: "mytrainings_delete",
    MY_FILTER_FAVORITES: "mytrainings_filter_favorites",
    MY_FILTER_RECENT: "mytrainings_filter_most_recent",
    MY_FILTER_MOST_USED: "mytrainings_filter_most_used",
    MY_LIKE: "mytrainings_like",
    MY_OPEN: "mytrainings_open",
    MY_RENAME: "mytrainings_rename",
    MY_START: "mytrainings_start_training",
    // Style training
    STYLE_EDIT_BUTTON: "style_training_edit_training_button",
    STYLE_START_BUTTON: "style_training_start_training_button",
    STYLE_POPUP_BROWSE: "style_training_popup_browse_collection",
    STYLE_POPUP_CREATE: "style_training_popup_create_new_design",
    STYLE_POPUP_EXPLORE: "style_training_popup_explore_new_trends",
    // Fabric training
    FABRIC_EDIT_BUTTON: "fabric_training_edit_training_button",
    FABRIC_START_BUTTON: "fabric_training_start_training_button",
    FABRIC_POPUP_BROWSE: "fabric_training_popup_browse_collection",
    FABRIC_POPUP_CREATE: "fabric_training_popup_create_new_design",
    FABRIC_POPUP_EXPLORE: "fabric_training_popup_explore_new_trends",
    FABRIC_TRY: "fabric_try_click",
    // Model training
    MODEL_POPUP_CREATE: "model_training_popup_create_new_design",
    MODEL_POPUP_EXPLORE: "model_training_popup_explore_new_trends",
  },

  // Pricing
  PRICING: {
    MONTHLY: "pricing_monthly_selected",
    YEARLY: "pricing_yearly_selected",
    CHOSE_PHOTOSHOOT_STARTER: "pricing_chose_plan_photoshoot_starter",
    CHOSE_DESIGN_STARTER: "pricing_chose_plan_design_starter",
    CHOSE_DESIGN_PRO: "pricing_chose_plan_design_pro",
    CHOSE_DESIGN_STUDIO: "pricing_chose_plan_design_studio",
    CHOSE_CREATIVE_SUITE: "pricing_chose_plan_creative_suite",
    PURCHASE_PHOTOSHOOT_STARTER: "purchase_plan_photoshoot_starter",
    PURCHASE_DESIGN_STARTER: "purchase_plan_design_starter",
    PURCHASE_DESIGN_PRO: "purchase_plan_design_pro",
    PURCHASE_DESIGN_STUDIO: "purchase_plan_design_studio",
    PURCHASE_CREATIVE_SUITE: "purchase_plan_creative_suite",
    // Fallback label for a purchase whose plan code is none of the five above — a legacy
    // `teams.package_type` (`starter` / `essentials` / `premium` on 12k+ prod teams) or an
    // unmapped subscription code — so it produces an event instead of none, as the old
    // if/else-if chain with no else did. The raw code rides along as a `package_type` param.
    PURCHASE_OTHER: "purchase_plan_other",
    ENTERPRISE_CONTACT: "pricing_enterprise_contact_us",
    ENTERPRISE_CONTACT_CLOSE: "pricing_enterprise_contact_us_close",
    MOBILE_SWIPE_NEXT: "pricing_mobile_swiper_swipe_next",
    MOBILE_SWIPE_PREV: "pricing_mobile_swiper_swipe_previous",
    UPGRADE_PLAN: "Upgrade_Plan",
    UPGRADE_POPUP: "Upgrade_Plan_Pop-up",
    // REF-6076: existing subscriber changing the seat count of the plan they already hold
    // (a Stripe seat change, not a checkout — so deliberately NOT a `pricing_chose_plan_*`).
    SEAT_CHANGE_INITIATED: "pricing_seat_change_initiated",
  },

  // Extra credit add-on packages (Account → Plan & Billing). GA4-only funnel —
  // deliberately NO Google Ads conversion here (existing-customer expansion, not
  // acquisition); coordinate with Yigit before wiring any conversion action.
  BUY_CREDITS: {
    MODAL_OPEN: "buy_credits_modal_open",
    CHECKOUT_START: "buy_credits_checkout_start",
    PURCHASE_SUCCESS: "buy_credits_purchase_success",
  },

  // Homepage
  HOME: {
    CREATE_BUTTON: "create_button_clicked",
    CREATE_SIDEBAR: "create_sidebar_navigation_click",
    HEADER_START_NEW: "header_start_new",
    CANVAS_PROJECTS_SEE_ALL: "homepage_canvas_projects_see_all",
    CAROUSEL_SCROLL: "homepage_carousel_scroll",
    COLLECTION_ITEM_CLICK: "homepage_collection_item_on_click",
    COLLECTIONS_DELETE: "homepage_collections_delete",
    COLLECTIONS_LIKE: "homepage_collections_like",
    COLLECTIONS_OPEN_FOLDER: "homepage_collections_open_folder",
    COLLECTIONS_RENAME: "homepage_collections_rename",
    COLLECTIONS_SEE_ALL: "homepage_collections_see_all",
    DESIGN_HISTORY_EDIT: "homepage_design_history_edit",
    DESIGN_HISTORY_SAVE: "homepage_design_history_save_collection",
    DESIGN_HISTORY_SEE_ALL: "homepage_design_history_see_all",
    GET_STARTED_CARD: "homepage_get_started_card_click",
    MYTRAININGS_CREATE_WITH: "homepage_mytrainings_create_design_with",
    MYTRAININGS_DELETE: "homepage_mytrainings_delete",
    MYTRAININGS_LIKE: "homepage_mytrainings_like",
    MYTRAININGS_OPEN: "homepage_mytrainings_open",
    MYTRAININGS_RENAME: "homepage_mytrainings_rename",
    MYTRAININGS_SEE_ALL: "homepage_mytrainings_see_all",
    MYTRAININGS_START: "homepage_mytrainings_start_training",
    ONBOARDING_TOGGLE: "homepage_onboarding_toggle",
    PHOTOSHOOTS_ITEM_DOWNLOAD: "homepage_photoshoots_item_download",
    PHOTOSHOOTS_ITEM_EDIT: "homepage_photoshoots_item_edit",
    PHOTOSHOOTS_ITEM_SAVE: "homepage_photoshoots_item_save_collection",
    TRENDS_EXPLORE_ALL: "homepage_trends_explore_all",
    WEBINAR_BANNER: "homepage_webinar_banner_click",
  },

  // Navigation
  NAV: {
    HOME: "nav_home_click",
    CREATE: "nav_create_click",
    EDIT: "nav_edit_click",
    TRAIN: "nav_train_click",
    MY_CREATIONS: "nav_mycreations_click",
    EXPLORE_TRENDS: "nav_exploretrends_click",
    // Sidebar → Explore Trends → Retail Brands (REF-6762).
    // Sidebar → Trends → Runway Data (was Trends Data; the event keeps its name).
    DISCOVER_HISTORY: "nav_discover_history_click",
    // Sidebar → Trends → Runway Trends (`/runway-trends`).
    RUNWAY_TRENDS: "nav_runway_trends_click",
    // Sidebar → Trends → Retail Trends (`/retail-trends`, REF-7160).
    RETAIL_TRENDS: "nav_retail_trends_click",
    // Sidebar → Trends → Retail Data (`/retail-data`).
    RETAIL_DATA: "nav_retail_data_click",
    // Sidebar → Reports (`/reports`, REF-7271) — shown only to a team with a shared report.
    REPORTS: "nav_reports_click",
  },

  // Assets
  ASSETS: {
    FILTER_ALL: "assets_filter_all",
    FILTER_GENDER: "assets_filter_gender",
    FILTER_LOOK: "assets_filter_look",
    FILTER_MOOD: "assets_filter_mood",
    FILTER_PATTERN: "assets_filter_pattern",
    MULTI_SELECT_TOGGLE: "assets_multi_select_toggle",
    ADD_ASSET_SELECT_TYPE: "myassets_addasset_select_type",
    ADD_ASSET_UPLOAD: "myassets_addasset_uploadasset",
    CONVERT_TECHNICAL: "myassets_convert_to_technical_drawing",
    DELETE: "myassets_delete",
    EDIT: "myassets_edit",
    // REF-6533 — a Design Asset re-typed from the detail overlay. Params: `from`, `to` (object_type values),
    // `mode`: "in_place" (file_type edit on the same row) | "convert" (create destination + delete source,
    // used whenever a modern fabric is on either side).
    CHANGE_TYPE: "myassets_change_type",
    EXPORT_ROAR: "myassets_export_to_roar",
    // My Assets → Created export action (REF-6093). JOOR needs no click event here —
    // `EVENTS.JOOR.EXPORT_START` already fires with `surface` + the selection count for the menu and the
    // bulk paths alike.
    EXPORT_ROAR_CREATED: "created_export_roar",
    NAVIGATE_INTEGRATIONS: "navigate_to_integrations_from_assets",
    EDIT_CARD_CLICK: "edit_asset_card_click",
    EDIT_UPLOAD: "edit_asset_upload",
    EDIT_FILTER: "edit_filter_click",
    EDIT_SEARCH: "edit_search_submit",
  },

  // Discover
  // REF-6069 dropped DESELECT_FILTER / REMOVE_APPLIED_FILTER / SELECT_ALL along with the
  // unreachable multi-select → Generate Trend Analysis flow they instrumented.
  // Trend History (REF-6832)
  TREND_HISTORY: {
    TAB_SWITCH: "trend_history_tab_switch",
    SHOW_ALL: "trend_history_show_all",
    MOVER_OPEN: "trend_history_mover_open",
    TREE_TYPE_OPEN: "trend_history_tree_type_open",
    TREE_VIEW_SWITCH: "trend_history_tree_view_switch",
    CONCEPT_CREATE: "trend_history_concept_create",
  },

  DISCOVER: {
    FILTER_CITY: "discover_filter_city",
    FILTER_DESIGNER: "discover_filter_designer",
    // FILTER_SEASON now fires for the season *category* half of the split; the year
    // half has its own event (REF-6030).
    FILTER_SEASON: "discover_filter_season",
    FILTER_SEASON_YEAR: "discover_filter_season_year",
    FILTER_SOURCE: "discover_filter_source",
    ITEM_FILTER: "discover_item_filter",
    ITEM_OPEN: "discover_item_open",
    ITEM_RECREATE: "discover_item_recreate",
    SEARCH_SUGGESTION: "discover_search_suggestion_click",
    // Explore Trends → Retail Brands facets (REF-6762). Param: `value` — the comma-joined selection
    // (the week is `<iso_year>-<iso_week>`, "" for every week).
    FILTER_RETAIL_BRAND: "discover_filter_retail_brand",
    FILTER_RETAIL_GENDER: "discover_filter_retail_gender",
    FILTER_RETAIL_CATEGORY: "discover_filter_retail_category",
    FILTER_RETAIL_WEEK: "discover_filter_retail_week",
  },

  // Trends
  // REF-6069 also dropped GENERATE ("trends_generate_click") and IMAGE_SELECT
  // ("trends_image_select_click") — both fired only from the multi-select selection bar
  // and its card checkboxes, so neither has been reachable (or reported to GA4) since
  // /discover became desktop-only.
  TRENDS: {
    FILTER_CLICK: "trends_filter_click",
    SCROLL: "trends_scroll_scroll",
    TAG_CLICK: "trends_tag_click",
    // Trend Agent chat widget
    AGENT_OPEN: "trend_agent_open",
    AGENT_DISMISS: "trend_agent_dismiss",
    AGENT_MESSAGE_SENT: "trend_agent_message_sent",
    AGENT_SUGGESTION_CLICK: "trend_agent_suggestion_click",
    AGENT_ADD_TO_INSPIRATION: "trend_agent_add_to_inspiration",
    AGENT_NEW_CHAT: "trend_agent_new_chat",
    AGENT_RESULT_ZOOM: "trend_agent_result_zoom",
    // Create Moodboard — new create flow (REF-5966). TYPE_SELECT carries a `type`
    // param (images | colors | fabrics | prints); CREATE carries per-type counts.
    MOODBOARD_TYPE_SELECT: "moodboard_type_select_click",
    MOODBOARD_UPLOAD_REFERENCES: "moodboard_upload_references_click",
    MOODBOARD_SELECT_ASSETS: "moodboard_select_assets_click",
    // Moodboard folder upload (REF-6804). PICK carries `source` (folder | files) and the accepted
    // count; START carries the file count and the `mode` (multi | single).
    MOODBOARD_FOLDER_PICK: "moodboard_folder_pick_click",
    MOODBOARD_FOLDER_START: "moodboard_folder_start_click",
    MOODBOARD_CREATE: "moodboard_create_click",
    // Moodboard usage (REF-6825) — fired by `utils/moodboardEvents.pingMoodboardEvent` alongside the
    // BE usage event. Params: `source` (my | curated | catalogue), `surface` (moodboards_page |
    // design_canvas). OPEN fires once per open session (`utils/moodboardOpenSessions`), not per render.
    MOODBOARD_OPEN: "moodboard_open",
    // Download PDF clicked — react-to-print cannot confirm a saved file, so never call it a download.
    MOODBOARD_PDF_EXPORT_REQUESTED: "moodboard_pdf_export_requested",
    MOODBOARD_IMAGE_DOWNLOAD: "moodboard_image_download",
    // Moodboard USE (REF-6936), same params. SEND_TO_CANVAS: the board is attached to the Design
    // Canvas; GENERATE_WITH: a generation / Edit & Variate request with the board attached was
    // ACCEPTED (one per request); ADD_ASSET: one of its assets is put to use; SAVE_ASSET: one of its
    // assets / its palette is saved to the user's library.
    MOODBOARD_SEND_TO_CANVAS: "moodboard_send_to_canvas",
    MOODBOARD_GENERATE_WITH: "moodboard_generate_with",
    MOODBOARD_ADD_ASSET: "moodboard_add_asset",
    MOODBOARD_SAVE_ASSET: "moodboard_save_asset",
  },

  // Collections
  COLLECTIONS: {
    FILTER_POPULAR: "collection_filter_most_popular",
    FILTER_RECENT: "collection_filter_most_recent",
    SEARCH: "collections_search",
    ITEM_CLICK: "mycollections_collection_item_on_click",
    DELETE: "mycollections_delete",
    OPEN_FOLDER: "mycollections_open_folder",
    RENAME: "mycollections_rename",
  },

  // My Creations
  MY_CREATIONS: {
    DATE_FILTER: "mycreations_date_filter_click",
    LIKE: "mycreations_like_click",
    MULTI_SELECT_TOGGLE: "mycreations_multi_select_toggle",
    SEARCH: "mycreations_search_submit",
  },

  // Brand DNA
  BRAND_DNA: {
    FAVOURITE_TOGGLE: "brand_dna_favourite_toggle",
    SEARCH: "brand_dna_search_submit",
    SORT: "brand_dna_sort_click",
    FILTER_FAVORITES: "brand_dnas_filter_favorites",
    FILTER_MODEL: "brand_dnas_filter_model",
    FILTER_RECENT: "brand_dnas_filter_most_recent",
    FILTER_MOST_USED: "brand_dnas_filter_most_used",
    FILTER_STYLE: "brand_dnas_filter_style",
    SEARCH_LIST: "brand_dnas_search",
    // Fired once per confirmed delete on a Brand DNA surface (Fabric Trainings /
    // Models; formerly the retired /brand-dnas library), with a `type` param
    // (style | fabric | model) since the three tabs hit three different
    // endpoints, and `was_processing` so a stuck/failed report being cleared is
    // distinguishable from a deliberate cleanup of a finished one. REF-5983.
    DELETE: "brand_dnas_delete",
  },

  // Brand Kits — a kit built from uploaded files (REF-6865), the moodboard folder upload's two events on
  // the New Brand Kit page. FOLDER_PICK carries `source` (picker | drop) and the accepted count;
  // FOLDER_START the file count and the form's item / colour counts.
  BRAND_KITS: {
    FOLDER_PICK: "brand_kit_folder_pick_click",
    FOLDER_START: "brand_kit_folder_start_click",
  },

  // Canvas / Creation tools
  CANVAS: {
    ANIMATION_ADD_ANGLES: "animation_add_angles_click",
    ANIMATION_CANCEL: "animation_cancel_click",
    ANIMATION_DURATION_SELECT: "animation_duration_select",
    // Studio Animate: a suggested shot of the same product added back as a reference angle.
    ANIMATION_SUGGESTED_ANGLE_ADD: "animation_suggested_angle_add_click",
    ANIMATION_TRY: "animation_try_click",
    BACKGROUND_ASSET_UPLOAD: "background_asset_upload",
    BACKGROUND_MODE_TOGGLE: "background_mode_toggle",
    BACKGROUND_PROMPT_SUBMIT: "background_prompt_submit",
    BACKGROUND_REMOVE: "background_remove_click",
    BACKGROUND_REPLACE: "background_replace_click",
    COLOR_THEME: "Color_Theme",
    CREATE_PATTERN: "create_pattern_click",
    CREATE_REDESIGN_ASSET: "create_redesign_asset_click",
    POSE_EDIT_MODE_TOGGLE: "pose_edit_mode_toggle",
    POSE_EDIT_PROMPT_UPLOAD: "pose_edit_prompt_upload",
    TAILORING: "Tailoring_Event",
    MAGIC_SELECT_MENU_MINUS_BRUSH: "magic_menu_minus_brush",
    MAGIC_SELECT_MENU_PLUS_BRUSH: "magic_menu_plus_brush",
  },

  // Try-on
  TRYON: {
    PAGE_VIEW: "tryon_page_view",
    VIEW_360: "tryon_360view_button",
    BACKGROUND_SELECT: "tryon_background_select",
    CHOOSE_ANGLE: "tryon_choose_angle",
    GARMENT_SELECT: "tryon_garment_select_asset",
    MODEL_SELECT: "tryon_model_select",
    POSE_SELECT: "tryon_pose_select",
    UPLOAD_IMAGE2PROMPT: "tryon_promptarea_upload_image2prompt",
    RESULT_ACTIONS: "tryon_result_item_actions",
    SUBMIT_ACCEPTED: "tryon_submit_accepted",
    SUBMIT_ERROR: "tryon_submit_error",
  },

  // Ghost photoshoot
  GHOST: {
    PAGE_VIEW: "ghost_page_view",
    ADD_IMAGE_UPLOADED: "ghost_add_image_uploaded",
    CHOOSE_BACKGROUND: "ghost_choose_background",
    CHOOSE_OUTPUT_TYPE: "ghost_choose_output_type",
    SUBMIT_ACCEPTED: "ghost_submit_accepted",
    SUBMIT_ERROR: "ghost_submit_error",
    IMAGE_TYPE_CONFIRMED: "ghost_image_type_confirmed",
    IMAGE_EDIT: "ghost_image_edit",
    IMAGE_DELETE: "ghost_image_delete",
  },

  // User / Feedback
  USER: {
    FEEDBACK: "User_Feedback",
    CONTACT_US: "Contact_Us",
    NEW_FEATURE_POPUP: "New_Feature_Pop_up",
    NEW_FEATURE_POPUP_SHOWN: "New_Feature_Pop_up_Shown",
    PROFILE_AVATAR: "profile_avatar_click",
    PROMPT_BEAUTIFY: "prompt_assistant_beautfy_prompt",
    PROMPT_DESCRIBE: "prompt_assistant_describe_image",
    SIZE_ERROR: "Size_Error",
    START_NEW: "Start_New",
    LANGUAGE_CHANGED: "language_changed",
  },

  // Marketplace
  MARKETPLACE: {
    CREATE: "Marketplace_Create",
  },

  // Products
  PRODUCTS: {
    ADD_SELECT_TYPE: "products_addproduct_select_type",
    ADD_UPLOAD: "products_addproduct_upload",
    DELETE: "products_delete",
    EDIT: "products_edit",
    EXPORT_ROAR: "products_export_to_roar",
    FILTER_GENDER: "products_filter_gender",
    ITEM_CLICK: "products_item_click",
    MULTI_SELECT_TOGGLE: "products_multi_select_toggle",
    SEARCH: "products_search",
  },

  // Fabric tools
  FABRIC_TOOL: {
    ATTRIBUTE_UPLOAD: "fabric_attribute_upload_upload",
    BRUSH_TOOL: "fabric_brush_tool_click",
    CANCEL: "fabric_cancel_click",
    LASSO_TOOL: "fabric_lasso_tool_click",
    MAGIC_SELECT_CLICK: "fabric_magic_selection_click",
    UPLOAD: "fabric_upload_upload",
    PATTERN_UPLOAD: "pattern_upload_upload",
  },

  // Techpack (legacy asset filtering)
  TECHPACK: {
    SEARCH: "techpack_assets_search",
    FILTER_ALL: "techpack_filter_all",
    FILTER_MATERIALS: "techpack_filter_materials",
    FILTER_TECHNICAL_DRAWINGS: "techpack_filter_technicaldrawings",
  },

  // Tech Pack Page (TechPackPage full workflow)
  TECH_PACK: {
    // P0 — Core funnel & outcomes
    PAGE_VIEW: "tp_page_view",
    PROJECT_CREATE_START: "tp_project_create_start",
    PROJECT_CREATE_SUCCESS: "tp_project_create_success",
    PROJECT_CREATE_ERROR: "tp_project_create_error",
    IMAGE_UPLOAD: "tp_image_upload",
    IMAGE_CONVERSION_STARTED: "tp_image_conversion_started",
    IMAGE_CONVERSION_COMPLETED: "tp_image_conversion_completed",
    IMAGE_CONVERSION_ERROR: "tp_image_conversion_error",
    GENERATION_STARTED: "tp_generation_started",
    GENERATION_COMPLETED: "tp_generation_completed",
    GENERATION_ERROR: "tp_generation_error",
    VIEW_GENERATION_STARTED: "tp_view_generation_started",
    VIEW_GENERATION_COMPLETED: "tp_view_generation_completed",
    TAB_CHANGE: "tp_tab_change",
    SAVE_SUCCESS: "tp_save_success",
    SAVE_ERROR: "tp_save_error",
    PDF_DOWNLOAD_SUCCESS: "tp_pdf_download_success",
    PDF_DOWNLOAD_ERROR: "tp_pdf_download_error",
    PDF_PREVIEW_OPENED: "tp_pdf_preview_opened",
    EXCEL_DOWNLOAD_SUCCESS: "tp_excel_download_success",
    EXCEL_DOWNLOAD_ERROR: "tp_excel_download_error",
    SVG_DOWNLOAD: "tp_svg_download",

    // P1 — User behavior patterns
    IMAGE_REMOVED: "tp_image_removed",
    IMAGE_TYPE_CONFIRMED: "tp_image_type_confirmed",
    UPLOAD_VIEW_TAB_SWITCH: "tp_upload_view_tab_switch",
    ASSET_SELECTED: "tp_asset_selected",
    VIEW_OPTION_SELECTED: "tp_view_option_selected",
    VIEW_REGENERATE_CLICKED: "tp_view_regenerate_clicked",
    SIZE_RANGE_CHANGED: "tp_size_range_changed",
    BODY_PRESET_SELECTED: "tp_body_preset_selected",
    SUPPLIER_COUNTRY_SELECTED: "tp_supplier_country_selected",
    // Image intake (D-STR-techpack-image-intake): images sent for analysis, the draft is ready, the draft confirmed.
    INTAKE_STARTED: "tp_intake_started",
    INTAKE_LEGACY_STARTED: "tp_intake_legacy_started",
    INTAKE_ASSETS_PICKED: "tp_intake_assets_picked",
    INTAKE_DRAFT_READY: "tp_intake_draft_ready",
    INTAKE_CONFIRMED: "tp_intake_confirmed",
    PRODUCT_DETAILS_EDITED: "tp_product_details_edited",
    PROJECT_RENAMED: "tp_project_renamed",
    BRAND_SELECTED: "tp_brand_selected",
    COLOR_EXTRACTION_STARTED: "tp_color_extraction_started",
    COLOR_EXTRACTION_COMPLETED: "tp_color_extraction_completed",
    MEASUREMENT_LINE_ADDED: "tp_measurement_line_added",
    PLACEMENT_POINT_ADDED: "tp_placement_point_added",
    VIEW_MODE_SWITCHED: "tp_view_mode_switched",
    PDF_EDITOR_OPENED: "tp_pdf_editor_opened",
    ORDER_SHEET_ROW_ADDED: "tp_order_sheet_row_added",

    // P2 — Granular interactions
    GENERATE_BACK_FROM_FRONT: "tp_generate_back_from_front",
    GENERATE_SIDE_FROM_FRONT: "tp_generate_side_from_front",
    FABRIC_ROW_ADDED: "tp_fabric_row_added",
    FABRIC_ROW_REMOVED: "tp_fabric_row_removed",
    SIZE_CHART_ROW_ADDED: "tp_size_chart_row_added",
    SIZE_CHART_ROW_REMOVED: "tp_size_chart_row_removed",
    SIZE_CHART_TABLE_APPLIED: "tp_size_chart_table_applied",
    COST_ITEM_ADDED: "tp_cost_item_added",
    COST_ITEM_REMOVED: "tp_cost_item_removed",
    COLOR_VARIATION_ADDED: "tp_color_variation_added",
    COLOR_VARIATION_REMOVED: "tp_color_variation_removed",
    COLOR_ENTRY_ADDED: "tp_color_entry_added",
    COLOR_ENTRY_REMOVED: "tp_color_entry_removed",
    ARTWORK_FABRIC_VARIATION_ADDED: "tp_artwork_fabric_variation_added",
    ARTWORK_FABRIC_VARIATION_REMOVED: "tp_artwork_fabric_variation_removed",
    ARTWORK_TRIMS_VARIATION_ADDED: "tp_artwork_trims_variation_added",
    ARTWORK_TRIMS_VARIATION_REMOVED: "tp_artwork_trims_variation_removed",
    CARE_INSTRUCTION_CHANGED: "tp_care_instruction_changed",
    PATTERN_BLOCK_DOWNLOADED: "tp_pattern_block_downloaded",
    // Tech Pack Agent (visual-first engine). New names — no existing value is reused or renamed.
    GARMENT_3D_VIEWED: "tp_garment_3d_viewed",
    GARMENT_3D_DOWNLOADED: "tp_garment_3d_downloaded",
    PATTERN_SHEET_MODE_SWITCHED: "tp_pattern_sheet_mode_switched",
    PATTERN_PIECE_DOWNLOADED: "tp_pattern_piece_downloaded",
    // REF-6677 — the AAMA/ASTM DXF of the pattern pieces (steps["7"].pattern_dxf), for garment CAD.
    PATTERN_DXF_DOWNLOADED: "tp_pattern_dxf_downloaded",
    // 2026-10-02 — the Original / Fit-corrected pattern block version switch (steps["7"].block_versions).
    PATTERN_VERSION_SWITCHED: "tp_pattern_version_switched",
    FLAT_SKETCH_DOWNLOADED: "tp_flat_sketch_downloaded",
    SVG_EDITOR_OPENED: "tp_svg_editor_opened",
    SVG_EDITOR_DOWNLOADED: "tp_svg_editor_downloaded",
    // REF-5971 — edited sketch persisted back onto the project's view (PNG + SVG in one history entry).
    SVG_EDITOR_SAVED: "tp_svg_editor_saved",
    MEASUREMENT_LINE_REMOVED: "tp_measurement_line_removed",
    PLACEMENT_POINT_REMOVED: "tp_placement_point_removed",
    TAB_SCROLL: "tp_tab_scroll",
    ORDER_SHEET_ROW_REMOVED: "tp_order_sheet_row_removed",
    CERTIFICATES_EDITED: "tp_certificates_edited",
    GARMENT_INFO_EDITED: "tp_garment_info_edited",
    SIZE_SELECTED: "tp_size_selected",
    PROJECT_LOADED: "tp_project_loaded",
    LOGO_UPLOADED: "tp_logo_uploaded",
    LOGO_REMOVED: "tp_logo_removed",
    ORDER_SHEET_IMAGE_ADDED: "tp_order_sheet_image_added",
    DRAWING_HISTORY_VIEWED: "tp_drawing_history_viewed",
    DRAWING_RESTORED: "tp_drawing_restored",
    DRAWING_SVG_CONVERTED: "tp_drawing_svg_converted",
    // Marker / re-lay (pattern_block_stitch, 2026-09-25)
    MARKER_DIRECTION_TOGGLED: "tp_marker_direction_toggled",
    MARKER_RELAY_STARTED: "tp_marker_relay_started",
    MARKER_RELAY_COMPLETED: "tp_marker_relay_completed",
    MARKER_RELAY_FAILED: "tp_marker_relay_failed",
    // 3D fit view (steps[7].fit_3d)
    FIT_3D_VIEWED: "tp_fit_3d_viewed",
    FIT_3D_SIZE_SELECTED: "tp_fit_3d_size_selected",
    FIT_3D_MODE_TOGGLED: "tp_fit_3d_mode_toggled",
  },

  // Generic download from the shared DownloadButton (standard / 2K raster + mp4, every surface).
  // Fire via trackDownload with { format, resolution, surface, is_zip }. Split by `surface` in GA4.
  DOWNLOAD: "download",

  // Download as 4K — shared across surfaces (Design Canvas, Photoshoot, History, Tech Pack).
  // Fire with params { surface, file_type, format, resolution } for breakdowns.
  DOWNLOAD_4K: {
    START: "download_4k_start",
    ALREADY_4K: "download_4k_already",
    SUCCESS: "download_4k_success",
    FAILED: "download_4k_failed",
  },

  // Design Canvas (DesignStudioPage)
  DESIGN_CANVAS: {
    // P0 — Generation funnel & core outcomes
    PAGE_VIEW: "dc_page_view",
    SESSION_START: "dc_session_start",
    EXIT: "dc_exit",
    EXIT_SIGNAL: "dc_exit_signal",
    API_ERROR: "dc_api_error",
    GEN_MODE_SELECT: "dc_gen_mode_select",
    GEN_PROMPT_SUBMIT: "dc_gen_prompt_submit",
    GEN_RESULT_SUCCESS: "dc_gen_result_success",
    GEN_RESULT_ERROR: "dc_gen_result_error",
    EDIT_TOOL_SELECT: "dc_edit_tool_select",
    EDIT_APPLY: "dc_edit_apply",
    EDIT_RESULT_SUCCESS: "dc_edit_result_success",
    NODE_DOWNLOAD: "dc_node_download",
    NODE_DOWNLOAD_AS: "dc_node_download_as",
    // REF-7156 — "Edit drawing" on a technical drawing node; `has_svg` = opened at once vs. converted first.
    EDIT_DRAWING: "dc_edit_drawing",
    // REF-7156 — a drawing edited in the overlay was saved as a new edit version.
    EDIT_DRAWING_SAVED: "dc_edit_drawing_saved",
    NOTIFY_ENABLE: "dc_notify_enable",
    NOTIFY_SKIP: "dc_notify_skip",

    // P1 — User behavior patterns
    GEN_INPUT_ADD: "dc_gen_input_add",
    GEN_INPUT_REMOVE: "dc_gen_input_remove",
    GEN_ASPECT_RATIO: "dc_gen_aspect_ratio_change",
    GEN_COUNT: "dc_gen_count_change",
    NODE_DELETE: "dc_node_delete",
    NODE_DUPLICATE: "dc_node_duplicate",
    NODE_ADD_TO_FRAME: "dc_node_add_to_frame",
    NODE_USE_AS_REF: "dc_node_use_as_reference",
    NODE_USE_AS_REF_PANEL: "dc_node_use_as_reference_panel",
    NODE_USE_IN_FRAME: "dc_node_use_in_frame",
    SIDEBAR_OPEN: "dc_sidebar_panel_open",
    SIDEBAR_CLOSE: "dc_sidebar_panel_close",
    CANVAS_DROP_ASSET: "dc_canvas_drop_asset",
    CANVAS_DROP_TO_PANEL: "dc_canvas_drop_to_panel",
    EDIT_VERSION_SELECT: "dc_edit_version_select",
    HISTORY_SEARCH: "dc_history_search",
    HISTORY_DOWNLOAD: "dc_history_download",
    HISTORY_ITEM_CLICK: "dc_history_item_click",

    // P2 — Granular interactions
    CONTEXT_MENU_OPEN: "dc_canvas_context_menu",
    NODE_CROP: "dc_node_crop",
    NODE_CROP_COMPLETE: "dc_node_crop_complete",
    NODE_REMOVE_BG: "dc_node_remove_bg",
    NODE_CONVERT_SKETCH: "dc_node_convert_sketch",
    // REF-6907 — the node menu's Like / Unlike (`liked`: the state asked for).
    NODE_LIKE: "dc_node_like",
    CANVAS_ADD_FRAME: "dc_canvas_add_frame",
    CANVAS_UPLOAD_IMAGE: "dc_canvas_upload_image",
    GEN_BRAND_DNA_SELECT: "dc_gen_brand_dna_select",
    GEN_BRAND_DNA_UNSELECT: "dc_gen_brand_dna_unselect",
    GEN_RESOLUTION: "dc_gen_resolution_change",
    PROJECT_RENAME: "dc_project_rename",
    BRUSH_UNDO: "dc_edit_brush_undo",
    BRUSH_REDO: "dc_edit_brush_redo",
    BRUSH_CLEAR: "dc_edit_brush_clear",
    DENIM_LASER_START: "dc_denim_laser_start",
    EDIT_ANIMATE_APPLY: "dc_edit_animate_apply",
    EDIT_MASK_TOOL_SELECT: "dc_edit_mask_tool_select",
    EXTRACT_PRINT_OPEN: "dc_extract_print_open",
    // Seamless steering panel. SEAMLESS_START params: is_refine, repeat ("auto" when omitted),
    // has_prompt, has_reference — never the instruction text or an image url.
    SEAMLESS_OPEN: "dc_seamless_open",
    SEAMLESS_START: "dc_seamless_start",
    EXTRACT_PRINT_START: "dc_extract_print_start",
    // REF-6677 — Extract BoM's cost estimate: cost-reduction suggestions requested (free), and one applied
    // (opens Edit & Variate with its prompt; the generation itself is tracked by EDIT_APPLY).
    COST_VARIATIONS_REQUEST: "dc_cost_variations_request",
    COST_VARIATION_APPLY: "dc_cost_variation_apply",

    // Multi-select bottom action bar (REF-6345) — 2+ valid image/generation nodes selected
    BULK_PHOTOSHOOT_START: "dc_bulk_photoshoot_start",
    BULK_TECH_PACK_START: "dc_bulk_tech_pack_start",
    BULK_DOWNLOAD: "dc_bulk_download",
    BULK_DELETE: "dc_bulk_delete",
  },

  // Photoshoot Studio
  STUDIO: {
    PAGE_VIEW: "ps_studio_page_view",
    PROJECT_NAME_SET: "ps_studio_project_name_set",
    TAB_CHANGE: "ps_studio_tab_change",
    PRODUCT_UPLOAD: "ps_studio_product_upload",
    PRODUCT_REMOVE: "ps_studio_product_remove",
    PRODUCT_ASSET_SELECT: "ps_studio_product_asset_select",
    // Bulk-upload observability. Before these two, a Model Change upload that failed left no
    // record anywhere: the only telemetry was a dynamic `<pathname>_upload_limit_error` GA4
    // name with no counts, and the transport failure had no Sentry event at all — which is why
    // the 200-300 image report could not be diagnosed after the fact.
    // UPLOAD_REJECTED fires when we refuse files BEFORE uploading (expected, not an error).
    // Params: reason, surface, shooting_type, existing_count, incoming_count, accepted_count,
    // rejected_count, has_zip, limit.
    UPLOAD_REJECTED: "ps_studio_upload_rejected",
    // UPLOAD_BATCH_RESULT fires once per settled batch — this is the one that answers
    // "did the upload work". Params: outcome ("success" | "partial" | "failure"),
    // requested_count, succeeded_count, failed_count, duration_ms, has_zip, surface,
    // shooting_type. `partial` is the case that was previously invisible: successes were
    // discarded along with the failure and reported as a generic error.
    UPLOAD_BATCH_RESULT: "ps_studio_upload_batch_result",
    // One settled resumable ZIP upload (Model Change archive, up to the BE ceiling). Params: outcome
    // ("success" | "cancelled" | "interrupted" | "failed"), error_kind, size_mb, confirmed_mb,
    // resume_count, duration_ms, shooting_type. Never the file name or the session url.
    ZIP_UPLOAD_RESULT: "ps_studio_zip_upload_result",
    SHOOTING_TYPE_SELECT: "ps_studio_shooting_type_select",
    // REF-6184 — a complementary product added to a combination row. Params: source
    // ("file_drop" | "card_drop" | "picker") + count. The two drop sources are the point: before this
    // ticket a dragged file could not become a complementary product at all, and a dragged card only
    // landed if released on the 504px slot strip. `source` is how we tell whether the drag paths get used.
    COMPLEMENTARY_ADD: "ps_studio_complementary_add",
    COMBINATION_MODE_CHANGE: "ps_studio_combination_mode_change",
    PROJECT_CREATE_START: "ps_studio_project_create_start",
    PROJECT_CREATE_SUCCESS: "ps_studio_project_create_success",
    PROJECT_CREATE_ERROR: "ps_studio_project_create_error",
    PROJECT_RENAME: "ps_studio_project_rename",
    // REF-6045 — user reported a generated image via the feedback widget. Params:
    // sentiment, category, has_comment. Submission only; the fix itself is
    // operator-driven and never happens client-side.
    IMAGE_FEEDBACK_SUBMIT: "ps_studio_image_feedback_submit",
    BRIEF_MODEL_SELECT: "ps_studio_brief_model_select",
    BRIEF_POSE_SELECT: "ps_studio_brief_pose_select",
    BRIEF_BACKGROUND_SELECT: "ps_studio_brief_background_select",
    BRIEF_STYLING_SELECT: "ps_studio_brief_styling_select",
    BRIEF_PROPS_SELECT: "ps_studio_brief_props_select",
    BRIEF_IMAGE_SIZE_CHANGE: "ps_studio_brief_image_size_change",
    BRIEF_RESOLUTION_CHANGE: "ps_studio_brief_resolution_change",
    BRIEF_INCLUDE_FLAT_TOGGLE: "ps_studio_brief_include_flat_toggle",
    BRIEF_INCLUDE_GHOST_TOGGLE: "ps_studio_brief_include_ghost_toggle",
    BRIEF_FLAT_GHOST_VIEWS_CHANGE: "ps_studio_brief_flat_ghost_views_change", // REF-5901 front/back for flat/ghost
    // Product views: the seven product-only deliverables, their reuse-vs-generate mode, and saving
    // them onto a product. `reused` on the plan event is what tells us whether the reuse path is
    // actually used — the whole feature exists to avoid paying for a shot twice.
    BRIEF_PRODUCT_VIEW_TOGGLE: "ps_studio_brief_product_view_toggle",
    BRIEF_PRODUCT_VIEW_MODE_CHANGE: "ps_studio_brief_product_view_mode_change",
    SAVE_AS_PRODUCT: "ps_studio_save_as_product",
    // Several products in one call. `failed` is the number the backend refused per product —
    // partial success is the contract here, so a bulk save that reports 0 failures and one that
    // reports 3 are both ordinary outcomes and only this event can tell them apart.
    SAVE_AS_PRODUCT_BULK: "ps_studio_save_as_product_bulk", // params: products, saved, failed
    // A preset carrying product views was applied and the user chose a mode per view. `reused`
    // is how many they set to `from_product` — the number that says whether the reuse half of
    // the feature is actually used, which no other event can answer (the studio request only
    // reports the modes, not that they came from a preset).
    PRESET_VIEW_MODES_CONFIRMED: "ps_studio_preset_view_modes_confirmed", // params: views, reused
    BRIEF_INCLUDE_DETAIL_TOGGLE: "ps_studio_brief_include_detail_toggle", // REF-5901 close-up per product
    BRIEF_COUNT_CHANGE: "ps_studio_brief_count_change",
    BRIEF_GENDER_FILTER: "ps_studio_brief_gender_filter",
    BRIEF_MODEL_DESELECT: "ps_studio_brief_model_deselect",
    EDIT_MENU_EXPAND_IMAGE_CLICK: "ps_studio_edit_menu_expand_image_click",
    EXPAND_IMAGE_START: "ps_studio_expand_image_start",
    EXPAND_IMAGE_COMPLETE: "ps_studio_expand_image_complete",
    // REF-5933 — non-generative colour adjustment on a studio result. APPLY fires once
    // per saved image, so APPLY/OPEN is the tool's completion rate.
    EDIT_ADJUST_OPEN: "ps_studio_edit_adjust_open",
    EDIT_ADJUST_APPLY: "ps_studio_edit_adjust_apply",
    // REF-5573 — non-generative badge/logo stamp on a studio result. Same OPEN/APPLY pair as
    // adjust, so APPLY/OPEN is the completion rate; APPLY carries layer_count, which answers
    // whether anyone actually stamps more than one badge (and therefore whether the multi-badge
    // affordance earns its keep).
    EDIT_OVERLAY_OPEN: "ps_studio_edit_overlay_open",
    EDIT_OVERLAY_APPLY: "ps_studio_edit_overlay_apply",
    // REF-6205 — Pose Presets (Select Poses tab: save the active product's pose selection as a
    // reusable, optionally team-shared, preset; apply/share/delete an existing one). All four fire
    // on SUCCESS (after the API call resolves without `_handled`/error), never on click, so failed
    // calls don't inflate adoption numbers.
    PRESET_SAVE: "ps_studio_preset_save", // params: is_shared (the new preset's shared state), preset_id
    PRESET_APPLY: "ps_studio_preset_apply", // params: preset_id
    // is_shared is the NEW state after the toggle (true = now shared with team, false = now personal).
    PRESET_SHARE_TOGGLE: "ps_studio_preset_share_toggle", // params: preset_id, is_shared
    PRESET_DELETE: "ps_studio_preset_delete", // params: preset_id
    // REF-6367 Studio Polish — one AI light/colour pass over finished frames. All three entry points
    // share the endpoint, so `surface` ("bulk" | "edit_panel") is the only thing that tells the
    // one-click single-frame use apart from a 100+ frame batch. QUOTE fires once per confirm-modal
    // open, START only after the 202, so START/QUOTE is the price-sticker abandon rate — the number
    // that says whether 5 credits/frame is the blocker.
    POLISH_MENU_CLICK: "ps_studio_polish_menu_click", // edit-panel tool menu entry opened
    POLISH_QUOTE: "ps_studio_polish_quote", // params: surface, eligible_count, skipped, total_credit
    POLISH_START: "ps_studio_polish_start", // params: surface, image_count, credit_amount
    POLISH_CANCEL: "ps_studio_polish_cancel", // params: cancelled_queued
    POLISH_RETRY_FAILED: "ps_studio_polish_retry_failed", // params: image_count
    // REF-6560 — bulk Restore Details. params: queued, target_count, skipped_count, credit_per_output.
    // `credit_per_output` is 0 today; a non-zero value in GA4 is the signal that the endpoint started
    // reserving credit while the FE still has no pre-submit price confirmation.
    RESTORE_DETAILS_BULK_START: "ps_studio_restore_details_bulk_start",
  },

  // JOOR export (REF-5934) — pushes already-generated renders onto the customer's JOOR catalog.
  // Permission-gated (`export_to_joor`) and free, so these measure adoption of an integration only a
  // handful of accounts hold, NOT a revenue funnel. `surface` distinguishes the screen the renders
  // were picked from — the full vocabulary is studio_detail | canvas_history | design_canvas |
  // assets_created. Keep this list in step with the `surface="…"` literals
  // (`grep -rn 'surface="' src`), or GA4 analysis silently misses a screen.
  JOOR: {
    EXPORT_START: "joor_export_start",
    // Fires once per run when polling reaches done, carrying the status_counts breakdown — so
    // COMPLETE/START is the delivery rate, and the no_external_id count is the size of the
    // "customer hasn't set their codes yet" problem.
    EXPORT_COMPLETE: "joor_export_complete",
    EXPORT_ERROR: "joor_export_error",
    EXPORT_RETRY: "joor_export_retry",
    FILTER_STATUS: "joor_filter_status",
    FIX_CODE_CLICK: "joor_fix_code_click",
  },

  // Miro two-way asset sharing (REF-6702). Gated on the `miro` module. `surface` names the screen the
  // images were picked from (design_history | canvas_history | studio_detail | design_canvas |
  // assets | trend_detail | account). Never put the OAuth code/state or board names in params.
  MIRO: {
    CONNECT_START: "miro_connect_start",
    CONNECT_SUCCESS: "miro_connect_success",
    CONNECT_DENIED: "miro_connect_denied",
    CONNECT_ERROR: "miro_connect_error",
    DISCONNECT: "miro_disconnect",
    SEND_OPEN: "miro_send_open",
    SEND_SUBMIT: "miro_send_submit",
    SEND_COMPLETE: "miro_send_complete", // { surface, created, failed }
    SEND_ERROR: "miro_send_error",
    IMPORT_OPEN: "miro_import_open",
    IMPORT_SUBMIT: "miro_import_submit",
    IMPORT_COMPLETE: "miro_import_complete", // { imported, duplicate, failed }
    IMPORT_ERROR: "miro_import_error",
    MOODBOARD_OPEN: "miro_moodboard_open",
    MOODBOARD_SUBMIT: "miro_moodboard_submit", // { images, prints, fabrics, colors }
    MOODBOARD_COMPLETE: "miro_moodboard_complete", // { created, failed }
    SEND_BACKGROUND: "miro_send_background", // { kind }
    CANVAS_OPEN: "miro_canvas_open", // { surface, scope }
    CANVAS_SUBMIT: "miro_canvas_submit", // { scope, count, new_board }
    CANVAS_COMPLETE: "miro_canvas_complete", // { created, skipped, failed }
    RETRY: "miro_job_retry", // { kind }
    CANVAS_IMPORT_OPEN: "miro_canvas_import_open",
    CANVAS_IMPORT_SUBMIT: "miro_canvas_import_submit",
    CANVAS_IMPORT_COMPLETE: "miro_canvas_import_complete", // { imported, skipped }
    BOARD_MOODBOARD_OPEN: "miro_board_moodboard_open",
    BOARD_MOODBOARD_SUBMIT: "miro_board_moodboard_submit", // { whole_board, split }
    BOARD_MOODBOARD_COMPLETE: "miro_board_moodboard_complete", // { images, included }
    BOARD_CREATED: "miro_board_created", // { surface }
  },

  // Studio-based Ghost feature (2-tab wizard at /photoshoot-studio → /v1/ghost_photoshoot/*).
  // Distinct ghost_studio_ prefix so it never collides with the legacy combined-page GHOST.* events.
  GHOST_STUDIO: {
    PAGE_VIEW: "ghost_studio_page_view",
    TAB_CHANGE: "ghost_studio_tab_change",
    SELECT_VIEWS: "ghost_studio_select_views",
    SELECT_OUTPUT_TYPES: "ghost_studio_select_output_types",
    SELECT_INPUT_TYPE: "ghost_studio_select_input_type",
    SELECT_BACKGROUND: "ghost_studio_select_background",
    SELECT_IMAGE_SIZE: "ghost_studio_select_image_size",
    SELECT_RESOLUTION: "ghost_studio_select_resolution",
    TOGGLE_DETAIL: "ghost_studio_toggle_detail", // Detail (close-up) on/off — one extra render per product
    PROJECT_CREATE_START: "ghost_studio_project_create_start",
    PROJECT_CREATE_SUCCESS: "ghost_studio_project_create_success",
    PROJECT_CREATE_ERROR: "ghost_studio_project_create_error",
  },

  // Ask Refabric chatbot (Phase 2.5 — server-side retrieval logs land
  // in chatbot_retrieval_logs; these are the FE-side complement so we
  // can join GA4 traffic against BE retrieval quality).
  //
  // Coordinate with Yigit (GTM container GTM-KMQZ7CSX) before adding
  // these to dashboards / conversion paths — he owns the GA4 setup.
  CHATBOT: {
    SEND: "chatbot_send", // user submitted a question
    FIRST_CHUNK: "chatbot_first_chunk", // first streamed token arrived — time-to-first-byte signal
    DONE: "chatbot_done", // assistant turn completed normally
    CANCEL: "chatbot_cancel", // user clicked stop
    ABANDON: "chatbot_abandon", // 30s without a chunk — likely network / BE stall
    ERROR: "chatbot_error", // explicit error frame or HTTP error
    OPEN: "chatbot_open", // panel opened (pairs with close events for engagement)
    CLOSE: "chatbot_close",
    NEW_CHAT: "chatbot_new_chat", // user explicitly started a fresh chat
    // Fire with { capability_id, route }. This is the outcome metric for the capability
    // graph (REF-6112): it measures whether the bot's answers actually take people
    // somewhere, and which destinations it routes to. A capability that never gets clicked
    // is either never asked about or being described badly.
    NAV_CLICK: "chatbot_nav_click",
    // An authorized reviewer snapshotted one answer for human review (REF-6217 P1-0). Fire
    // with { page_route, has_nav }. Low volume by design — the affordance is allowlisted —
    // so this is a health signal for the review loop, not an engagement metric: zero over a
    // week means corrections are not being captured, whatever the answer quality actually is.
    REVIEW_CAPTURE: "chatbot_review_capture",
  },

  // Academy (courses, watch flow, completion certificate). Fire with { course_id } (+ chapter_index
  // for chapter completion). Funnel: course_open → course_start → chapter_complete* → course_complete
  // → certificate_view → certificate_download / certificate_share_linkedin.
  ACADEMY: {
    COURSE_OPEN: "academy_course_open", // course detail viewed
    COURSE_START: "academy_course_start", // Start / Continue / Review → enter the watch page
    CHAPTER_COMPLETE: "academy_chapter_complete", // a chapter marked complete (Next / Complete)
    COURSE_COMPLETE: "academy_course_complete", // every chapter done
    CERTIFICATE_VIEW: "academy_certificate_view", // certificate modal opened
    CERTIFICATE_DOWNLOAD: "academy_certificate_download", // certificate PNG downloaded
    CERTIFICATE_SHARE_LINKEDIN: "academy_certificate_share_linkedin", // shared to LinkedIn
    NAME_ADDED: "academy_certificate_name_added", // full name saved from the certificate prompt
  },

  // Guided workflows (REF-6217). Output only — nothing in the guidance runtime ever *reads* an
  // analytics event to decide where the user is or whether a flow finished; that comes from the
  // feature's own published state. The funnel is:
  //   guide_started → guide_step_shown* (+ guide_blocked* where people stall) → guide_completed
  // with guide_exited as the alternative ending. `guide_step_completed` is deliberately absent —
  // it is derivable from the next step_shown.
  // Fit Models (fit profiles), 2026-10-11.
  FIT_MODELS: {
    PAGE_VIEW: "fm_page_view",
    PROFILE_OPENED: "fm_profile_opened",
    PROFILE_DELETED: "fm_profile_deleted", // { size_count }
    CHART_UPLOADED: "fm_chart_uploaded", // { file_type }
    IMPORT_PREVIEWED: "fm_import_previewed", // { has_data, issue_count }
    PROFILE_CREATED: "fm_profile_created", // { size_count, source: "import" | "manual" }
    PROFILE_UPDATED: "fm_profile_updated", // { revision }
    PROFILE_COPIED: "fm_profile_copied", // { owner_kind } — a template / team chart copied into the user's own (v5)
    REVISION_CONFLICT: "fm_revision_conflict",
    AVATAR_GENERATED: "fm_avatar_generated", // { size, pose }
    TECH_PACK_FIT_MODEL_SELECTED: "tp_fit_model_selected", // { size, has_avatar }
  },
  // Reports shared with a team (REF-7267). `kind` is the report's registry kind, e.g. "hm_supplier_list".
  REPORTS: {
    OPENED: "report_opened", // the report's data loaded on its page. { kind }
    EXCEL_DOWNLOADED: "report_excel_downloaded", // the sheet reached the browser. { kind, product_count }
  },
  GUIDE: {
    STARTED: "guide_started", // start() activated a run. Fire with { flow, source }
    STEP_SHOWN: "guide_step_shown", // the feature reported a different step. { flow, step }
    BLOCKED: "guide_blocked", // edge-triggered, not per render: where and why users stall. { flow, step, reason }
    COMPLETED: "guide_completed", // the feature reported its terminal step. { flow, duration_ms }
    EXITED: "guide_exited", // the user dismissed a still-running guide. { flow, step }
  },
} as const;

export type EventName = string;
